import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import DeleteReportDialog from "../components/DeleteReportDialog";
import {
  ChartBar, Plus, Sparkle, CircleNotch, Trash, UsersThree, Clock, X, ArrowRight,
} from "@phosphor-icons/react";

const EXAMPLES = [
  "Top 10 clients by revenue this year — bar chart + table, with a short summary",
  "Jobs by status as a pie chart and active jobs as a stat card",
  "Monthly revenue trend with total revenue and total hours",
  "Asset Intelligence warnings table and assets by category",
];

function CreateModal({ onClose, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reports", { prompt: prompt.trim(), title: title.trim() || undefined });
      toast.success("Report generated");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="report-create-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={busy ? undefined : onClose} />
      <div className="relative w-full max-w-lg rounded-sm border border-neutral-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#7C3AED]/10"><Sparkle size={18} weight="fill" className="text-[#7C3AED]" /></div>
            <h2 className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">New AI report</h2>
          </div>
          <button data-testid="report-create-close" onClick={onClose} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">Report title (optional)</label>
            <input data-testid="report-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Q2 Client Performance"
              className="w-full rounded-sm border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#7C3AED]" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">Describe the report you want</label>
            <textarea data-testid="report-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
              placeholder="Describe the dashboard — metrics, charts, tables, time period…"
              className="w-full resize-none rounded-sm border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#7C3AED]" />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Try one of these</p>
            {EXAMPLES.map((ex) => (
              <button key={ex} data-testid="report-example" onClick={() => setPrompt(ex)}
                className="block w-full rounded-sm border border-neutral-200 px-3 py-2 text-left text-sm text-neutral-600 transition-all duration-200 hover:border-[#7C3AED] hover:text-[#7C3AED]">
                {ex}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4">
          <button onClick={onClose} disabled={busy} className="rounded-sm border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Cancel</button>
          <button data-testid="report-generate-btn" onClick={create} disabled={busy || !prompt.trim()}
            className="inline-flex items-center gap-2 rounded-sm bg-[#7C3AED] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6B21C8] disabled:opacity-50">
            {busy ? <><CircleNotch size={15} className="animate-spin" /> Generating…</> : <><Sparkle size={15} weight="fill" /> Generate report</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/reports").then((r) => setReports(r.data.reports)).catch(() => setReports([]));
  useEffect(() => { load(); }, []);

  const askDelete = (e, r) => {
    e.stopPropagation();
    setToDelete(r);
  };

  return (
    <Layout wide>
      <div data-testid="reports-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">AI</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Reports</h1>
            <p className="mt-2 max-w-3xl text-base text-neutral-600">
              Build live dashboards from your imported data. Describe what you want and the AI assembles charts,
              tables and summaries — then refresh any report to keep it current.
            </p>
          </div>
          <button data-testid="new-report-btn" onClick={() => setCreating(true)}
            className={`inline-flex items-center gap-2 rounded-sm bg-[#0A0A0A] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#7C3AED] ${user?.role === "admin" ? "" : "hidden"}`}>
            <Plus size={16} weight="bold" /> New report
          </button>
        </div>

        {reports === null ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : reports.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-sm border border-dashed border-neutral-300 bg-white py-16 text-center" data-testid="reports-empty">
            <div className="flex h-14 w-14 items-center justify-center rounded-sm bg-[#7C3AED]/10"><ChartBar size={28} weight="bold" className="text-[#7C3AED]" /></div>
            <p className="font-heading text-xl font-black tracking-tight text-[#0A0A0A]">No reports yet</p>
            {user?.role === "admin" ? (
              <>
                <p className="max-w-sm text-sm text-neutral-500">Create your first AI-generated dashboard — try "Top 10 clients by revenue with a bar chart and table".</p>
                <button data-testid="new-report-empty-btn" onClick={() => setCreating(true)} className="mt-2 inline-flex items-center gap-2 rounded-sm bg-[#7C3AED] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6B21C8]"><Sparkle size={15} weight="fill" /> Generate a report</button>
              </>
            ) : (
              <p className="max-w-sm text-sm text-neutral-500">No reports have been shared with you yet. Your account manager can publish dashboards here.</p>
            )}
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {reports.map((r) => (
              <button key={r.id} data-testid={`report-card-${r.id}`} onClick={() => navigate(`/reports/${r.id}`)}
                className="group flex flex-col items-start gap-3 rounded-sm border border-neutral-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex w-full items-start justify-between gap-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#0055FF]/10"><ChartBar size={20} weight="bold" className="text-[#0055FF]" /></div>
                  <div className="flex items-center gap-1.5">
                    {r.client_visible && <span className="inline-flex items-center gap-1 rounded-sm bg-[#059669]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#059669]"><UsersThree size={11} weight="bold" /> Client</span>}
                    {r.can_edit && (
                      <span onClick={(e) => askDelete(e, r)} data-testid={`report-delete-${r.id}`} className="cursor-pointer rounded-sm p-1 text-neutral-300 hover:bg-[#DC2626]/10 hover:text-[#DC2626]"><Trash size={15} /></span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="font-heading text-lg font-black tracking-tight text-[#0A0A0A] group-hover:text-[#0055FF]">{r.title}</p>
                  {r.description && <p className="mt-1 line-clamp-2 text-sm text-neutral-500">{r.description}</p>}
                </div>
                <div className="mt-auto flex w-full items-center justify-between pt-2 text-xs text-neutral-400">
                  <span>{r.widget_count} widget{r.widget_count === 1 ? "" : "s"} · {r.created_by_name || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} />{new Date(r.updated_at).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        <PageAdvice items={[
          "Reports pull from all imported data (clients, jobs, assets, timesheets, condition reports, Asset Intelligence).",
          "Open a report and press Refresh data to bring it up to date — great for live dashboards.",
          "Only the creator can edit or delete a report. Toggle 'Visible to clients' to surface it in the client portal.",
        ]} />
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(rep) => navigate(`/reports/${rep.id}`)} />}
      {toDelete && <DeleteReportDialog report={toDelete} onClose={() => setToDelete(null)} onDeleted={(id) => { setReports((p) => p.filter((r) => r.id !== id)); setToDelete(null); }} />}
    </Layout>
  );
}
